import React, { useCallback, useEffect, useState } from 'react';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { driveDownloadUrl, driveImageUrl } from '../../utils/drive';
import { fetchGallery, type Photo } from '../../utils/photos';

/**
 * THE PHOTOGRAPHS, IN PORTAL'S OWN DESIGN.
 *
 * Tom, previewing Bloomberg as a client: *"Photography is not appearing."*
 * It could not. The page embeds a Drive FILE, and every photography
 * deliverable carries a Drive FOLDER, which has no preview URL — so the
 * client was shown a black box and an "Open folder" button, and asked to
 * approve stills they had never seen. Audit §2.4.
 *
 * WHAT TOM ASKED FOR, 18 Sep: show the photos in Portal's own design;
 * download one; download all; **no per-photo comments**. Photography is a
 * hand-off, not a review cycle, and a comment thread on each of ninety-seven
 * frames is a different product.
 *
 * REJECTED: Drive's own `embeddedfolderview` grid in an iframe. Cheap, and it
 * is Google's styling sitting inside Portal's page.
 *
 * "DOWNLOAD ALL" OPENS DRIVE, AND SAYS SO. Drive has no public URL that zips
 * a folder — the zip in its web UI is a UI action with no stable link behind
 * it. Zipping server-side was considered and rejected: a 400-photo shoot is
 * gigabytes and will not survive an Edge Function's limits. So the button
 * says where it goes, because a control that does not do what it says is the
 * exact fault this redesign started from ("Simulated", audit §0).
 *
 * THE CONTACT SHEET IS SQUARE, THE LIGHTBOX IS NOT. Uniform square cells are
 * what makes a mixed set of portrait and landscape frames read as one sheet
 * rather than a ragged wall — the crop is a thumbnail convention and every
 * photographer expects it. The moment a frame is opened it is shown whole, at
 * its own shape, never cropped.
 *
 * THE SHEET CLOSES WITH A RULE. Below it on the page sit the client's own
 * decisions about this set — approve, ask for a change — and without the rule
 * "Show all 97" and "Happy with this" were two rows of near-identical buttons
 * thirty pixels apart, which is exactly the undifferentiated left-hand stack
 * this page has been corrected for twice already.
 */

/** Enough to fill the sheet without asking Drive for a hundred images at once. */
const FIRST_SHOWN = 24;

/** Asked of Drive, not drawn — roughly 2× the CSS size, so it stays sharp. */
const TILE_WIDTH = 600;
const FULL_WIDTH = 2000;
function Sheet({
  photos,
  onOpen
}: {
  photos: Photo[];
  onOpen: (index: number) => void;
}) {
  return <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4" data-id="element-41" data-mp="vcnsi7guhywm">
      {photos.map((photo, index) => <button key={photo.id} type="button" onClick={() => onOpen(index)} aria-label={`Open photograph ${index + 1}`} className="group relative block aspect-square w-full overflow-hidden bg-paperDeep" data-id="element-42" data-mp="14bhllnuyw75f" data-mpi={index}>
          <img src={driveImageUrl(photo.id, TILE_WIDTH)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" data-id="element-43" data-mp="2fq4vk57na9lv" />
        </button>)}
    </div>;
}
function Lightbox({
  photos,
  at,
  onMove,
  onClose
}: {
  photos: Photo[];
  at: number;
  onMove: (next: number) => void;
  onClose: () => void;
}) {
  const photo = photos[at];
  const step = useCallback((by: number) => {
    const next = (at + by + photos.length) % photos.length;
    onMove(next);
  }, [at, photos.length, onMove]);

  /* Arrow keys and Escape, because a full-screen image that only answers the
     mouse feels broken to anyone who looks at photographs for a living. */
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  /* The page behind must not scroll while this is open. */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  /* Fetch the neighbours quietly, so stepping through feels instant. */
  useEffect(() => {
    for (const by of [1, -1]) {
      const neighbour = photos[(at + by + photos.length) % photos.length];
      if (neighbour) new Image().src = driveImageUrl(neighbour.id, FULL_WIDTH);
    }
  }, [at, photos]);
  if (!photo) return null;
  const control = 'label flex h-11 items-center gap-2 px-4 text-paper/70 transition-colors duration-150 ease-out hover:text-paper';
  return (
    /* SOLID ink, not `bg-ink/97`. That class compiles to NOTHING in this
       build — the overlay came out fully transparent, the client page showed
       straight through the photograph, and the paper-coloured controls were
       invisible on paper. Text opacities (text-paper/70 and friends) do work;
       an opacity on `bg-ink` does not. Check a computed background before
       trusting one here. A lightbox should be a dark room anyway. */
    <div role="dialog" aria-modal="true" aria-label={`Photograph ${at + 1} of ${photos.length}`} className="fixed inset-0 z-50 flex flex-col bg-ink" data-id="element-44" data-mp="3l13qn1h9s9g4">
      <div className="flex shrink-0 items-center justify-between px-3 py-3 sm:px-6" data-id="element-45" data-mp="3tz5vxt81do97">
        <span className="label pl-2 text-paper/50" data-id="element-46" data-mp="2s3552w41jzi">
          {at + 1} / {photos.length}
        </span>
        <div className="flex items-center" data-id="element-47" data-mp="7lfksnitagg4">
          <a href={driveDownloadUrl(photo.id)} target="_blank" rel="noopener noreferrer" className={`${control} no-underline`} data-id="element-48" data-mp="cj7jc0dyp0ta">
            Download
          </a>
          <button type="button" onClick={onClose} className={control} aria-label="Close" data-id="element-49" data-mp="ljdrr7uszall">
            <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-50" data-mp="2ik072aebswnl" />
            <span className="hidden sm:inline" data-id="element-51" data-mp="2iifc4jo3nbp7">Close</span>
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-6 sm:px-16" data-id="element-52" data-mp="2djc6oy47j3gp">
        <img key={photo.id} src={driveImageUrl(photo.id, FULL_WIDTH)} alt="" className="max-h-full max-w-full object-contain" data-id="element-53" data-mp="2rey49toiavkq" />

        {photos.length > 1 && <>
            <button type="button" onClick={() => step(-1)} aria-label="Previous photograph" className="absolute left-0 top-1/2 flex h-14 w-12 -translate-y-1/2 items-center justify-center text-paper/50 transition-colors duration-150 ease-out hover:text-paper sm:w-14" data-id="element-54" data-mp="2wc6mfu9nmzlz">
              <ChevronLeftIcon className="h-7 w-7" strokeWidth={1.25} data-id="element-55" data-mp="1fyg5hmxfsgux" />
            </button>
            <button type="button" onClick={() => step(1)} aria-label="Next photograph" className="absolute right-0 top-1/2 flex h-14 w-12 -translate-y-1/2 items-center justify-center text-paper/50 transition-colors duration-150 ease-out hover:text-paper sm:w-14" data-id="element-56" data-mp="1bx4mw1czcvw8">
              <ChevronRightIcon className="h-7 w-7" strokeWidth={1.25} data-id="element-57" data-mp="s234j9gphq48" />
            </button>
          </>}
      </div>
    </div>
  );
}
export function Gallery({
  deliverableKey
}: {
  deliverableKey: string;
}) {
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [folderUrl, setFolderUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [at, setAt] = useState(-1);
  useEffect(() => {
    let live = true;
    setLoading(true);
    setShowAll(false);
    setAt(-1);
    void fetchGallery(deliverableKey).then(result => {
      if (!live) return;
      setPhotos(result.photos);
      setFolderUrl(result.folderUrl);
      setFailed(result.failed);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [deliverableKey]);
  const quiet = 'label border border-rule px-5 py-3.5 text-graphite no-underline transition-colors duration-150 ease-out hover:border-ink hover:text-ink';
  if (loading) {
    return <div className="flex min-h-[18rem] items-center justify-center border border-rule bg-paperDeep/40" data-id="element-58" data-mp="338kicj0ewaq0">
        <p className="label m-0 text-mute" data-id="element-59" data-mp="2ybkf0fuy5m2l">Loading the photographs…</p>
      </div>;
  }
  if (photos.length === 0) {
    return <div className="border border-rule bg-paperDeep/40 px-8 py-14 text-center" data-id="element-60" data-mp="2tdiv5spp5iq2">
        <p className="m-0 text-[1.05rem] leading-relaxed text-charcoal" data-id="element-61" data-mp="2ocv1eza32ns5">
          {failed ? 'The photographs could not be loaded just now.' : 'The photographs are not in the gallery yet.'}
        </p>
        {folderUrl && <a href={folderUrl} target="_blank" rel="noopener noreferrer" className={`${quiet} mt-7 inline-block`} data-id="element-62" data-mp="iiv52afcwwzf">
            Open the folder in Drive
          </a>}
      </div>;
  }
  const shown = showAll ? photos : photos.slice(0, FIRST_SHOWN);
  const remaining = photos.length - shown.length;
  return <div className="border-b border-rule pb-10" data-id="element-63" data-mp="2e8epui3mzsu5">
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3" data-id="element-64" data-mp="2e74o9ixks2ir">
        <p className="label m-0 text-mute" data-id="element-65" data-mp="1hj71717q13m0">
          {photos.length} {photos.length === 1 ? 'photograph' : 'photographs'}
        </p>
        {folderUrl && <a href={folderUrl} target="_blank" rel="noopener noreferrer" className="label text-graphite no-underline transition-colors duration-150 ease-out hover:text-ink" data-id="element-66" data-mp="1qitu0ymdx13g">
            Download all in Drive
          </a>}
      </div>

      <Sheet photos={shown} onOpen={setAt} data-id="element-67" data-mp="36x3l40i0h3xz" />

      {remaining > 0 && <button type="button" onClick={() => setShowAll(true)} className={`${quiet} mt-8`} data-id="element-68" data-mp="36z6ij5lhtzcq">
          Show all {photos.length}
        </button>}

      {at >= 0 && <Lightbox photos={photos} at={at} onMove={setAt} onClose={() => setAt(-1)} data-id="element-69" data-mp="1y2kkakeldz0y" />}
    </div>;
}